import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { ChevronDown, ChevronUp, Home, Map as MapIcon, Send, Share2 } from 'lucide-react';
import {
  CHAT_TEXT_MAX,
  DEFAULT_COURSES,
  NEEDS,
  careerEased,
  formatPence,
  wageFor,
  type Activity,
  type ActivityContent,
  type PlaceContent,
  type LifeView,
  type NeedKey,
  type Needs,
  type PersonAction,
  type PersonView,
  govPriceOf,
  govWageOf,
} from '@london-life/contracts';
import type { FriendlyError } from '@london-life/errors';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { lookToCharacter, toPreviewCharacter } from '@london-life/game/appearance';
import { PersonCard, PersonPicture } from '../people/person-card';
import { useCatalogue } from '../lib/content';
import { useGovEffects } from '../lib/gov';
import { MessageList } from '../chat/message-list';
import { useConversation } from '../chat/use-conversation';
import type { Realtime } from '../chat/use-realtime';
import { popupText } from '../phone/message-popup';
import { notify } from '../lib/alerts';
import { shareLink } from '../lib/share';
import { useBackLayer } from '../lib/back-stack';
import { VenueView, type VenuePerson } from './venue-view';

// how many players are drawn in the room at once (everyone is in People)
const DRAWN = 10;
const BUBBLE_MS = 6000;

const effectsOf = (activity: Activity) =>
  (Object.entries(activity.effects) as [NeedKey, number][])
    .filter(([, value]) => value !== 0)
    .map(([key, value]) => ({ key, text: `${value > 0 ? '+' : ''}${value} ${NEEDS[key].label}`, up: value > 0 }));

type Props = {
  place: PlaceContent;
  // what can be done here (published content)
  activities: ActivityContent[];
  life: LifeView;
  needs: Needs;
  walletPence: number;
  busyLabel: string | null;
  busyFor: number;
  sending: boolean;
  failure: FriendlyError | null;
  onDo: (activityId: string) => void;
  // a card that opens a desk (the flights desk at the airport, the station's ticket office) instead of taking time
  onOpen?: (what: 'flights' | 'trains' | 'ballot') => void;
  // back to the map, or home
  onClose: () => void;
  onHome: () => void;
  // shifts worked per job: the wage shown includes the player's pay rises
  shifts?: Record<string, number>;
  // lessons passed per course: a certificate for a job promotes sooner, so its wage is higher sooner
  coursesDone?: Record<string, number>;
  people: PersonView[];
  peopleBusy: boolean;
  onPerson: (lifeId: string, body: PersonAction) => Promise<unknown>;
  onChat: (lifeId: string) => void;
  realtime: Realtime;
  onPlaceConversation?: (id: string | null) => void;
  myUserId: string | null;
  // your @name over your head
  myUsername?: string | null;
  dataSaver?: boolean;
};

// A place, the way Lagos Life shows one: the room in 3D with the players here walking about, and the venue
// bar along the bottom: where you are, what is going on, a box to say something to everyone here, and tabs
// for the spots (what you can do), the chat and the people.
export function VenueScreen({
  place,
  activities,
  life,
  needs,
  walletPence,
  busyLabel,
  busyFor,
  sending,
  failure,
  onDo,
  onOpen,
  onClose,
  onHome,
  shifts = {},
  coursesDone = {},
  people,
  peopleBusy,
  onPerson,
  onChat,
  realtime,
  onPlaceConversation,
  myUserId,
  myUsername,
  dataSaver = false,
}: Props) {
  const catalogue = useCatalogue();
  const venue = place.interior;
  // the spots, with what can be done at each; anything not in a spot gets a spot of its own
  const spots = useMemo(() => {
    const byId = new Map(activities.map((activity) => [activity.id, activity]));
    const listed = venue.spots.map((spot) => ({
      ...spot,
      activities: spot.activities.flatMap((id) => byId.get(id) ?? []),
    }));
    const placed = new Set(listed.flatMap((spot) => spot.activities.map((activity) => activity.id)));
    const left = activities.filter((activity) => !placed.has(activity.id));
    return [
      ...listed.filter((spot) => spot.activities.length),
      ...(left.length ? [{ id: 'here', label: 'Here', icon: '📍', x: 0, z: 0, activities: left }] : []),
    ];
  }, [venue, activities]);
  const [folded, setFolded] = useState(false);
  const [tab, setTab] = useState<string | null>(null);
  const [personId, setPersonId] = useState<string | null>(null);
  const [doing, setDoing] = useState<string | null>(null);
  const [text, setText] = useState('');
  const busy = busyFor > 0;
  useBackLayer(personId !== null, () => setPersonId(null));
  useBackLayer(tab !== null, () => setTab(null));

  // the line under the name changes every few seconds
  const [line, setLine] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setLine((value) => value + 1), 7000);
    return () => clearInterval(timer);
  }, []);
  const ambient = venue.vibes.length ? venue.vibes[line % venue.vibes.length] : place.description;

  // the place's chat: the box below says something to everyone here; the Chat tab shows what was said
  const chat = useConversation({ kind: 'place', placeId: place.id }, realtime, { markRead: false, myUserId });
  const conversationId = chat.conversation?.id ?? null;
  useEffect(() => {
    onPlaceConversation?.(conversationId);
    return () => onPlaceConversation?.(null);
  }, [conversationId, onPlaceConversation]);
  // what someone just said shows over their head
  const [bubbles, setBubbles] = useState<Record<string, string>>({});
  const lastSeen = useRef<string | null | undefined>(undefined);
  const latest = chat.messages.at(-1);
  useEffect(() => {
    if (lastSeen.current === undefined) {
      lastSeen.current = latest?.id ?? null; // what was said before you came in stays in the Chat tab
      return;
    }
    if (!latest || latest.id === lastSeen.current || latest.kind === 'system') return;
    lastSeen.current = latest.id;
    const id = latest.mine ? 'me' : latest.sender.lifeId;
    setBubbles((current) => ({ ...current, [id]: popupText(latest) }));
    const timer = window.setTimeout(
      () =>
        setBubbles((current) => {
          const next = { ...current };
          delete next[id];
          return next;
        }),
      BUBBLE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [latest]);
  const say = async (event: FormEvent) => {
    event.preventDefault();
    const words = text.trim();
    if (!words) return;
    if (await chat.send(words, null)) setText('');
  };

  // the room: you, and up to ten of the players here
  const me = useMemo(() => toPreviewCharacter(life.appearance, life.firstName), [life.appearance, life.firstName]);
  const drawn: VenuePerson[] = useMemo(
    () =>
      people.slice(0, DRAWN).map((person) => ({
        id: person.lifeId,
        character: lookToCharacter(person.look, person.firstName),
        tag: person.username ? `@${person.username}` : person.firstName,
      })),
    [people],
  );
  // your character goes to the spot of what you are doing
  const doingSpot = spots.find((spot) =>
    spot.activities.some((activity) =>
      // what it is doing now (a queued one starts by itself); else what was just tapped
      busy && busyLabel !== null ? activity.label === busyLabel : activity.id === doing,
    ),
  );
  const spot = doingSpot ? { x: doingSpot.x, z: doingSpot.z } : null;
  const person = people.find((entry) => entry.lifeId === personId) ?? null;

  // pay and prices as the Mayor's policy has them (the server pays and charges the same)
  const gov = useGovEffects();
  const wageOf = (activity: Activity) =>
    activity.wagePence && activity.job
      ? govWageOf(
          wageFor(
            activity.wagePence,
            shifts[activity.id] ?? 0,
            careerEased(catalogue?.courses ?? DEFAULT_COURSES, coursesDone, activity.id),
          ),
          gov,
        )
      : activity.wagePence && govWageOf(activity.wagePence, gov);
  const share = async () => {
    const url = `${location.origin}${location.pathname}#${place.id}`;
    if ((await shareLink({ title: place.name, url })) === 'copied') notify('Link copied');
  };
  const openSpot = spots.find((item) => item.id === tab);
  const others = people.length;
  return (
    <div className="venue-screen" role="dialog" aria-modal="true" aria-label={`At ${place.name}`}>
      <VenueView
        placeId={place.id}
        interior={venue}
        me={me}
        myTag={myUsername ? `@${myUsername}` : life.firstName}
        onBlocked={() => notify('Something is in the way')}
        people={drawn}
        spot={spot}
        busy={busy}
        bubbles={bubbles}
        onTag={setPersonId}
        dataSaver={dataSaver}
      />
      {folded ? (
        <div className="venue-pill">
          <button type="button" className="venue-pill-name" onClick={() => setFolded(false)}>
            {place.icon} {place.name} <ChevronUp size={16} aria-hidden="true" />
          </button>
          <button type="button" aria-label="Back to the map" onClick={onClose}>
            <MapIcon size={18} />
          </button>
          <button type="button" aria-label="Go home" onClick={onHome}>
            <Home size={18} />
          </button>
        </div>
      ) : (
        <section className="venue-bar" aria-label={place.name}>
          <header className="venue-head">
            <PersonPicture person={{ firstName: life.firstName, look: life.appearance }} />
            <div className="venue-where">
              <h2>
                {place.icon} {place.name} <small>· {place.area}</small>
              </h2>
              <p>{ambient}</p>
            </div>
            <div className="venue-actions">
              <button type="button" aria-label="Share this place" onClick={() => void share()}>
                <Share2 size={17} />
              </button>
              <button type="button" aria-label="Back to the map" onClick={onClose}>
                <MapIcon size={17} />
              </button>
              <button type="button" aria-label="Go home" onClick={onHome}>
                <Home size={17} />
              </button>
              <button
                type="button"
                aria-label="Fold the bar"
                onClick={() => {
                  setTab(null);
                  setFolded(true);
                }}
              >
                <ChevronDown size={17} />
              </button>
            </div>
          </header>
          {failure && <ErrorNotice error={failure} />}
          <form className="venue-say" onSubmit={(event) => void say(event)}>
            <input
              type="text"
              aria-label="Say something to everyone here"
              placeholder={others ? `Say something to the ${others + 1} players here…` : 'Say something out loud…'}
              value={text}
              maxLength={CHAT_TEXT_MAX}
              onChange={(event) => setText(event.target.value)}
              autoComplete="off"
              enterKeyHint="send"
            />
            <button type="submit" aria-label="Say it" disabled={chat.sending || !text.trim()}>
              <Send size={17} />
            </button>
          </form>
          <nav className="venue-tabs" aria-label="In this place">
            {tab && (
              <button type="button" className="venue-tab-close" aria-label="Close" onClick={() => setTab(null)}>
                <ChevronDown size={16} />
              </button>
            )}
            {spots.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-pressed={tab === item.id}
                onClick={() => setTab(tab === item.id ? null : item.id)}
              >
                <span aria-hidden="true">{item.icon}</span> {item.label}
              </button>
            ))}
            <button type="button" aria-pressed={tab === 'chat'} onClick={() => setTab(tab === 'chat' ? null : 'chat')}>
              <span aria-hidden="true">💬</span> Chat
            </button>
            <button
              type="button"
              aria-pressed={tab === 'people'}
              onClick={() => setTab(tab === 'people' ? null : 'people')}
            >
              <span aria-hidden="true">👥</span> People <b>{others}</b>
            </button>
          </nav>
          {openSpot && (
            <div className="venue-cards" role="group" aria-label={openSpot.label}>
              {openSpot.activities.map((activity) => {
                const cost = govPriceOf(activity, gov);
                const tooTired = activity.minEnergy !== undefined && needs.energy < activity.minEnergy;
                const wage = wageOf(activity);
                // a desk: it opens at once and takes no time
                if (activity.opens)
                  return (
                    <button
                      type="button"
                      key={activity.id}
                      className="venue-card venue-card-desk"
                      disabled={sending || !onOpen}
                      onClick={() => {
                        setTab(null);
                        onOpen?.(activity.opens!);
                      }}
                    >
                      <span className="venue-card-top">
                        <span className="venue-card-icon" aria-hidden="true">
                          {activity.icon}
                        </span>
                        <span className="venue-card-meta">
                          <small>Opens the desk</small>
                        </span>
                      </span>
                      <strong>{activity.label}</strong>
                    </button>
                  );
                return (
                  <button
                    type="button"
                    key={activity.id}
                    className="venue-card"
                    disabled={sending || cost > walletPence}
                    onClick={() => {
                      // as in Lagos Life: the cards fold away and the character goes to do it (or it waits its turn)
                      if (!busy) setDoing(activity.id);
                      setTab(null);
                      onDo(activity.id);
                    }}
                  >
                    <span className="venue-card-top">
                      <span className="venue-card-icon" aria-hidden="true">
                        {activity.icon}
                      </span>
                      <span className="venue-card-meta">
                        <small>⏱ {activity.seconds}s</small>
                        <em className={wage ? 'earn' : ''}>
                          {wage ? `+${formatPence(wage)}` : cost ? formatPence(cost) : 'Free'}
                        </em>
                      </span>
                    </span>
                    <strong>{activity.label}</strong>
                    {tooTired ? (
                      <small className="venue-card-note">Too tired right now</small>
                    ) : (
                      <span className="venue-card-effects">
                        {effectsOf(activity).map((effect) => (
                          <i key={effect.key} className={effect.up ? 'up' : 'down'}>
                            {effect.text}
                          </i>
                        ))}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {tab === 'chat' && (
            <div className="venue-chat">
              <MessageList
                compact
                composer={false}
                messages={chat.messages}
                placeholder="Say something to everyone here"
                sending={chat.sending}
                failure={chat.failure}
                hasOlder={chat.hasOlder}
                onOlder={() => void chat.loadOlder()}
                onSend={chat.send}
                onEdit={chat.edit}
                onDelete={chat.remove}
                onReport={chat.report}
                myUserId={myUserId}
                emptyText="Nobody has said anything yet. Say hello."
              />
            </div>
          )}
          {tab === 'people' &&
            (people.length ? (
              <ul className="venue-people" aria-label="People here">
                {people.map((entry) => (
                  <li key={entry.lifeId}>
                    <button type="button" className="venue-person" onClick={() => setPersonId(entry.lifeId)}>
                      <PersonPicture person={entry} />
                      <span>
                        <b>{entry.username ? `@${entry.username}` : entry.firstName}</b>
                        <small>{entry.tie === 'stranger' ? 'Real player' : entry.tie.replace('_', ' ')}</small>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="place-soon">
                Nobody else right now. People show up here when they are in the same place as you.
              </p>
            ))}
          {!spots.length && tab === null && (
            <p className="place-soon">
              More to do here soon: {place.things.join(', ').toLowerCase()}. For now, look around and say hello.
            </p>
          )}
        </section>
      )}
      {person && (
        <div
          className="venue-sheet-layer"
          onClick={(event) => {
            if (event.target === event.currentTarget) setPersonId(null);
          }}
        >
          <div
            className="venue-sheet"
            role="dialog"
            aria-label={person.username ? `@${person.username}` : person.firstName}
          >
            <button type="button" className="venue-sheet-close" aria-label="Close" onClick={() => setPersonId(null)}>
              ×
            </button>
            <ul className="people-list">
              <PersonCard
                person={person}
                busy={peopleBusy || busy}
                onAct={onPerson}
                compact
                onChat={(lifeId) => {
                  setPersonId(null);
                  onChat(lifeId);
                }}
              />
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
